import { IRecord } from 'redux/records/types';

/**
 * Filters and sorts an array of records
 * @param records - Array of record objects to process
 * @returns The processed array of records
 */
export const filterAndSortRecords = (records: IRecord[]) => {
  return records;
};

/**
 * Formats a date into a short display format with time (DD/MM, HH:mm)
 * @param dateTime - Date object to format
 * @returns Formatted date string in British locale format
 * @example
 * timeShortDisplay(new Date('2025-04-07T20:02:40')) // Returns "07/04, 20:02"
 */
export const timeShortDisplay = (dateTime: Date) => {
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    day: 'numeric',
    month: 'numeric',
    hour12: false,
  };

  return dateTime.toLocaleString('en-GB', options);
};

/**
 * Formats a date string into DD/MM/YYYY format
 * @param dateString - ISO date string to format
 * @returns Formatted date string in British locale format, or empty string if input is falsy
 * @example
 * formatDate('2025-04-07') // Returns "07/04/2025"
 * formatDate('') // Returns ""
 */
export const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Finds an record by its ID in an array of records
 * @param records - Array of records to search through
 * @param record_id - ID of the record to find
 * @returns The matching record object or undefined if not found
 */
export const getRecord = (records: IRecord[], record_id: string) => records.find((record) => record.record_id === record_id);

/**
 * Validates an email address
 * @param email - The email address to validate
 * @returns true if the email address is valid, false otherwise
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

/**
 * Validates an Israeli mobile phone number
 * @param phone - The phone number to validate
 * @returns true if the phone number is valid or empty, false otherwise
 */
export const validateIsraeliPhone = (phone: string): boolean => {
  const digits = phone.replace(/\D/g, '');
  // Empty is valid (not required field)
  if (digits.length === 0) return true;
  // Must be exactly 10 digits starting with 05X
  return /^05\d{8}$/.test(digits);
};

/**
 * Formats a phone number with dashes
 * @param phone - The phone number to format
 * @returns The formatted phone number
 */
export function formatPhoneNumber(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Validates an Israeli ID number using the Luhn algorithm
 * @param id - The ID number to validate
 * @returns true if the ID is valid, false otherwise
 */
export function validateIsraeliId(id: string): boolean {
  // Remove any non-digit characters
  id = id.replace(/\D/g, '');
  // Pad 8-digit IDs with leading zero
  if (id.length === 8) id = '0' + id;
  if (id.length !== 9) return false;

  const digits = id.split('').map(Number);
  let sum = 0;

  for (let i = 0; i < 8; i++) {
    let digit = digits[i] * ((i % 2) + 1);
    digit = digit > 9 ? digit - 9 : digit;
    sum += digit;
  }

  return (10 - (sum % 10)) % 10 === digits[8];
}

/**
 * Validates an Israeli phone field with detailed validation messages
 * @param stringValue - The phone number to validate
 * @param t - Translation function for error messages
 * @returns Empty string if valid, otherwise an error message
 */
export function validatePhoneField(stringValue: string, t: (key: string) => string): string {
  const digits = stringValue.replace(/\D/g, '');
  if (!digits.startsWith('0')) {
    return t('validation.mustStartWithZero');
  } else if (!digits.startsWith('05')) {
    return t('validation.invalidPhone');
  } else if (digits.length < 10) {
    return t('validation.missingDigits');
  } else if (digits.length > 10) {
    return t('validation.valueTooLong');
  } else if (!validateIsraeliPhone(stringValue)) {
    return t('validation.invalidPhone');
  }
  return '';
}

/**
 * Helper function to check if a date string is valid
 * @param dateStr - The date string to validate
 * @returns true if the date is valid, false otherwise
 */
export function isValidDate(dateStr: string): boolean {
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
}

export function areArraysShallowEqual<T>(a?: ReadonlyArray<T> | null, b?: ReadonlyArray<T> | null): boolean {
  const aa = a ?? [];
  const bb = b ?? [];

  return aa.length === bb.length && aa.every((v, i) => v === bb[i]);
}

export const USER_TEXT_MAX_LENGTH = 50000; //TODO: Recheck.

// export function openInNewTab(url: string): Window | null {
//   return window.open(url, '_blank', 'noopener,noreferrer');
// }

export function openCenteredPopup80(url: string): Window | null {
  const w = Math.floor(window.screen.availWidth * 0.8);
  const h = Math.floor(window.screen.availHeight * 0.8);
  const left = Math.floor((window.screen.availWidth - w) / 2);
  const top = Math.floor((window.screen.availHeight - h) / 2);

  return window.open(url, '_blank', `noopener,noreferrer,width=${w},height=${h},left=${left},top=${top}`);
}
